// @ts-strict-ignore
import apiCalls from '~/helpers/apiCalls';
import { ResponseException } from '~/helpers/exceptionHandler';
import { formatTimestamp } from '~/helpers/formatting';

import {
  CURRENT_AGENT_CLOSE_ERROR,
  CURRENT_AGENT_PROMOTE_ERROR,
  INVESTIGATION_OPEN_ALERTS_CLOSE,
  INVESTIGATION_OPEN_ALERTS_CLOSE_ERROR,
} from '../constants';
import { prepareUnixTime } from '../queue/helpers';
import type { InvestigationAgent, InvestigationRow } from '../types';
import type {
  AssignAgentRequest,
  AssignAlertAgentRequest,
  CompleteAlertReviewRequest,
  CompleteAlertManualReviewRequest,
  UnassignAlertAgentRequest,
  InvestigationQueueRow,
  InvestigationResponse,
  InvestigationsQueueResponse,
  UnassignInvestigation,
  InvestigationQueueFilterResponse,
  InvestigationQueueSavedSearchesResponse,
  QueueQueryParams, InvestigationQueueFilters,
  InvestigationSummaryResponse,
  InvestigationQueueFiltersResponse,
  InvestigationEvidenceListResponse,
  StatusOption,
} from './types';
import { formatInvestigationStatuses } from '../utils';

const {
  investigations: {
    addEvidenceToInvestigation,
    getInvestigationsQueue,
    getInvestigationsQueueFilters,
    getInvestigation,
    getInvestigationSummary,
    getInvestigationEvidence,
    promoteToCase,
    reviewAndClose,
    getAssignableAgents,
    assignAgent,
    unassign,
    alerts: {
      assignAgent: assignAlertAgentCall,
      completeReview: completeAlertReviewCall,
      submitManualReview,
      unassignAgent: unassignAlertAgentCall,
    },
    submitInvestigationNote,
  },
  settings: {
    getSavedSearches,
    getTags,
  },
} = apiCalls;

export const processTags = response => {
  const tagsAndOutcomeReasons = [...response]
    .sort((a, b) => a.tagName.localeCompare(b.tagName))
    .map(tag => ({
      ...tag,
      isOutcomeReason: Boolean(tag.outcomes),
    }),
    );
  return { tagsAndOutcomeReasons };
};

export const fetchTagsAndOutcomes = async ({
  typhonDomain,
}) => {
  try {
    const response = await getTags({ typhonDomain });
    return response;
  } catch (e) {
    const message = e?.response?.status === 403
      ? 'Sorry, you don\'t have permissions to access this page'
      : 'Failed to load Tags and Outcome Reasons';

    throw new ResponseException(e?.response, null, { message });
  }
};

export const fetchInvestigationsQueueFilters = async ({
  typhonDomain,
}): Promise<InvestigationQueueFilterResponse> => {
  try {
    const investigations = await getInvestigationsQueueFilters({
      typhonDomain,
    });

    return investigations;
  } catch (e) {
    const message = e?.response?.status === 403
      ? 'Sorry, you don\'t have permissions to access this page'
      : 'Failed to load Investigations filters for queue';

    throw new ResponseException(e?.response, null, { message });
  }
};

export const fetchInvestigationsQueueSavedSearches = async (): Promise<
InvestigationQueueSavedSearchesResponse
> => {
  const page = 0;
  const savedSearchesData = await getSavedSearches({
    filterBy: {
      url_path: '/investigations/',
    },
    page,
  });
  const {
    data: {
      [page]: {
        data: savedSearchesPage,
      },
    },
  } = savedSearchesData;
  return savedSearchesPage;
};

export const fetchInvestigationsQueue = async ({
  queryParams,
  typhonDomain,
}): Promise<InvestigationsQueueResponse> => {
  try {
    const {
      actionedByAgents,
      investigationTokens,
      entityTokens,
      externalEntityIds,
      includeClosed,
      archived,
      assignedAgents,
      assignedInvestigationAgents,
      assignedAlertAgents,
      deviceIds,
      programNames,
      sources,
      entityNames,
      statuses,
      types,
      updatedAtFrom,
      updatedAtTo,
      caseReviewAtFrom,
      caseReviewAtTo,
      closedAtConfirmedRiskFrom,
      closedAtConfirmedRiskTo,
      closedAtDismissedFrom,
      closedAtDismissedTo,
      createdAtFrom,
      createdAtTo,
      customers,
      eventTypes,
      tagsAndOutcomeReasons,
      perPage = 20,
      pageIndex = 0,
      column,
      order,
      useNewSearch,
    } = queryParams as QueueQueryParams;

    const params = new URLSearchParams();
    params.set('pageNum', (pageIndex + 1).toString());
    params.set('numRowsPerPage', perPage.toString());

    if (column && order) {
      params.set('sortColumn', column.toString());
      params.set('sortOrder', order.toString());
    }

    const filters = {} as { [key: string]: number | string | boolean | string[] };

    if (archived !== 'all') {
      if (archived?.length) {
        filters.archived = archived === 'true';
      } else {
        filters.archived = false;
      }
    }

    if (typeof includeClosed === 'boolean') {
      filters.includeClosed = includeClosed;
    }

    if (eventTypes?.length) {
      filters.eventTypes = eventTypes;
    }

    if (investigationTokens?.length) {
      filters.investigationTokens = investigationTokens;
    }

    if (deviceIds?.length) {
      filters.deviceIds = deviceIds;
    }

    if (entityTokens?.length) {
      filters.entityTokens = entityTokens;
    }

    if (externalEntityIds?.length) {
      filters.externalEntityIds = externalEntityIds;
    }

    if (tagsAndOutcomeReasons?.length) {
      filters.tagsAndOutcomeReasons = tagsAndOutcomeReasons;
    }

    if (caseReviewAtFrom) {
      filters.caseReviewAtFrom = prepareUnixTime(caseReviewAtFrom);
    }
    if (caseReviewAtTo) {
      filters.caseReviewAtTo = prepareUnixTime(caseReviewAtTo);
    }
    if (closedAtConfirmedRiskFrom) {
      filters.closedAtConfirmedRiskFrom = prepareUnixTime(closedAtConfirmedRiskFrom);
    }
    if (closedAtConfirmedRiskTo) {
      filters.closedAtConfirmedRiskTo = prepareUnixTime(closedAtConfirmedRiskTo);
    }
    if (closedAtDismissedFrom) {
      filters.closedAtDismissedFrom = prepareUnixTime(closedAtDismissedFrom);
    }
    if (closedAtDismissedTo) {
      filters.closedAtDismissedTo = prepareUnixTime(closedAtDismissedTo);
    }
    if (createdAtFrom) {
      filters.createdAtFrom = prepareUnixTime(createdAtFrom);
    }
    if (createdAtTo) {
      filters.createdAtTo = prepareUnixTime(createdAtTo);
    }
    if (updatedAtFrom) {
      filters.updatedAtFrom = prepareUnixTime(updatedAtFrom);
    }
    if (updatedAtTo) {
      filters.updatedAtTo = prepareUnixTime(updatedAtTo);
    }

    if (assignedAgents?.length) {
      filters.assignedAgents = assignedAgents;
    }
    if (sources?.length) {
      filters.sources = sources;
    }
    if (assignedInvestigationAgents?.length) {
      filters.assignedInvestigationAgents = assignedInvestigationAgents;
    }
    // TODO - OPERATE1-3115: Clean up Piermont specific INV columns/filters
    if (actionedByAgents?.length) {
      filters.actionedBy = actionedByAgents;
    }
    if (assignedAlertAgents?.length) {
      filters.assignedAlertAgents = assignedAlertAgents;
    }

    if (programNames?.length) {
      filters.programNames = programNames;
    }

    if (entityNames?.length) {
      filters.entityNames = entityNames;
    }

    if (statuses?.length) {
      filters.statuses = statuses;
    }
    if (types?.length) {
      filters.types = types;
    }
    if (customers?.length) {
      filters.afefCustomerSlugs = customers;
    }
    params.set('q', JSON.stringify(filters));

    if (useNewSearch) {
      params.set('useNewSearch', useNewSearch.toString());
    }

    const investigations = await getInvestigationsQueue({
      queryParamsStr: params.toString(),
      typhonDomain,
    });

    return investigations;
  } catch (e) {
    const options = { cause: e?.response?.status };
    if (e?.response?.status === 403) {
      throw new Error('Sorry, you don\'t have permissions to access this page.', options);
    } else if (e?.response?.status === 404) {
      throw new Error('The Investigations queue could not be found', options);
    } else {
      throw new Error('Failed to load Investigations data', options);
    }
  }
};

export const fetchInvestigation = async ({
  investigationToken,
  typhonDomain,
  toggleInvestigationsSandboxFeatureFlag,
}): Promise<InvestigationResponse> => {
  try {
    const params = new URLSearchParams();
    const isSandbox = Boolean(toggleInvestigationsSandboxFeatureFlag);

    if (isSandbox) {
      params.set('get_field_is_sandbox', isSandbox.toString());
    }

    const rawInvestigation = await getInvestigation({
      investigationToken,
      typhonDomain,
      queryParamsStr: params.toString(),
    });

    return rawInvestigation;
  } catch (e) {
    let message = 'Failed fetching Investigation';

    if (e?.response?.status === 403) {
      message = 'Sorry, you don\'t have permissions to see this Investigation';
    }

    if (e?.response?.status === 404) {
      message = 'This Investigation could not be found';
    }

    throw new ResponseException(e?.response, null, { message });
  }
};

export const fetchInvestigationSummary = async ({
  investigationToken,
  typhonDomain,
}): Promise<InvestigationSummaryResponse> => {
  try {
    const rawInvestigationSummary = await getInvestigationSummary({
      investigationToken,
      typhonDomain,
    });

    return rawInvestigationSummary;
  } catch (e) {
    let message = 'Failed fetching Investigation Summary';

    if (e?.response?.status === 403) {
      message = 'Sorry, you don\'t have permissions to see this Investigation Summary';
    }

    if (e?.response?.status === 404) {
      message = 'This Investigation Summary could not be found';
    }

    throw new ResponseException(e?.response, null, { message });
  }
};

export const fetchInvestigationEvidence = async ({
  investigationToken,
  typhonDomain,
}): Promise<InvestigationEvidenceListResponse> => {
  try {
    const evidenceData = await getInvestigationEvidence({
      investigationToken,
      typhonDomain,
    });

    return evidenceData;
  } catch (e) {
    let message = 'Failed fetching Investigation Evidence';

    if (e?.response?.status === 403) {
      message = 'Sorry, you don\'t have permissions to see this Investigation Evidence';
    }

    if (e?.response?.status === 404) {
      message = 'This Investigation Evidence could not be found';
    }

    throw new ResponseException(e?.response, null, { message });
  }
};

export const promoteInvestigationToCase = async ({
  investigationToken,
  payload,
  typhonDomain,
}): Promise<InvestigationsQueueResponse> => {
  try {
    const investigations = await promoteToCase({
      investigationToken,
      payload,
      typhonDomain,
    });

    return investigations;
  } catch (e) {
    const options = { cause: e?.response?.status };

    if (
      e?.response?.status === 403
      && e?.responseError?.details?.message === CURRENT_AGENT_PROMOTE_ERROR
    ) {
      throw new Error(CURRENT_AGENT_PROMOTE_ERROR, options);
    } else if (e?.response?.status === 403) {
      throw new Error('Sorry, you don\'t have permissions to perform this action.', options);
    } else if (e?.response?.status === 404) {
      throw new Error('This Investigation could not be found', options);
    } else {
      throw new Error('Failed to promote to a case', options);
    }
  }
};

export const convertToInvestigationRow = (
  rows: InvestigationQueueRow[],
) => rows.map(queueRow => {
  const { entities, assignedAgent } = queueRow.Embedded;
  const entitiesList = Object.values(entities)
    .filter(ent => ent?.entityName);

  let entityType = 'person';
  let entityName = '—';
  let riskScore = 0;
  if (entitiesList.length > 0) {
    const entity = entitiesList.shift();
    entityType = entitiesList.length > 1 ? 'group' : entity.entityType;
    entityName = entity.entityName;
    riskScore = entity.riskScore;
  }

  let assignee = 'Unassigned';
  if (assignedAgent) {
    assignee = `${assignedAgent.firstName} ${assignedAgent.lastName}`;
  }

  const actionedBy = queueRow?.actionedBy || [];
  const alertAssignees = queueRow?.alertAssignees || [];
  const investigationStatus = queueRow.status || null;

  return {
    token: queueRow.investigationToken,
    entityType,
    entityName,
    riskScore,
    type: queueRow.type,
    createdAt: formatTimestamp(new Date(queueRow.createdAt)),
    escalatedAt: queueRow.escalatedAt ? formatTimestamp(new Date(queueRow.escalatedAt)) : null,
    closedAt: queueRow.closedAt ? formatTimestamp(new Date(queueRow.closedAt)) : null,
    archivedAt: queueRow.archivedAt ? formatTimestamp(new Date(queueRow.archivedAt)) : null,
    totalAlerts: queueRow.totalAlerts,
    assignee,
    updatedAt: formatTimestamp(new Date(queueRow.updatedAt)),
    assignees: [assignee],
    actionedBy,
    alertAssignees,
    events: queueRow.events,
    investigationOutcome: queueRow.outcome,
    investigationOutcomeColor: queueRow.outcomeColor ? `#${queueRow.outcomeColor}` : null,
    investigationOutcomeIcon: queueRow.outcomeIconName || queueRow.outcomeIcon || null,
    investigationStatusColor: queueRow.statusColor ? `#${queueRow.statusColor}` : null,
    investigationStatusIcon: queueRow.statusIcon || queueRow.statusIconName || null,
    investigationState: queueRow.state,
    investigationStatus,
    ...(queueRow?.organizationNameSlug
      ? { organizationNameSlug: queueRow.organizationNameSlug }
      : {}),
    ...(queueRow?.organizationName
      ? { organizationName: queueRow.organizationName }
      : {}),
    tags: (queueRow?.tags ?? []).map(tag => ({
      tagName: tag.tagName,
      tagColor: `#${tag.tagColor}`,
      isOutcomeReason: tag.isOutcomeReason,
    })),
  } as InvestigationRow;
});

export const convertToFilters = (
  filters: InvestigationQueueFiltersResponse,
  investigationTypesFeatureFlag: boolean,
): InvestigationQueueFilters => {
  delete filters.investigationTypes;

  const assignedAgents: InvestigationAgent[] = filters
    .assignedAgents.map(agent => {
      const subGroup = agent?.organizationName;
      return ({
        ...agent,
        ...({ subGroup }),
      });
    });
  const assignedAlertAgents: InvestigationAgent[] = filters?.assignedAlertAgents?.map(
    agent => {
      const subGroup = agent?.organizationName;
      return ({
        ...agent,
        ...({ subGroup }),
      });
    },
  ) || [];
  const assignedInvestigationAgents: InvestigationAgent[] = (
    filters?.assignedInvestigationAgents?.map(
      agent => {
        const subGroup = agent?.organizationName;
        return ({
          ...agent,
          ...({ subGroup }),
        });
      },
    ) || []
  );

  const formattedTypes: {
    type: string;
    useCase: string;
    subGroup?: string;
  }[] = filters.typesWithUseCases.map(type => {
    const subGroup = type.useCase;
    if (investigationTypesFeatureFlag) {
      return ({
        ...type,
        ...({ subGroup }),
      });
    }
    return ({
      ...type,
    });
  });

  const filteredTypes = formattedTypes.filter((
    obj,
    index,
    self,
  ) => index === self.findIndex(t => {
    if (t.type === obj.type) {
      if (investigationTypesFeatureFlag) {
        return t.subGroup === obj.subGroup;
      }
      return true;
    }
    return false;
  }));

  const outcomeMetaByName = new Map<string, {
    investigation_status: boolean;
    escalated_investigation_status: boolean;
    outcome_color?: string;
    outcome_icon?: string;
  }>();
  const allowedStatusNames = new Set<string>();
  const statusOptions = filters.statusOptions ?? [];

  statusOptions.forEach((statusOption: StatusOption) => {
    if (!statusOption || !statusOption.status) return;
    const name = statusOption.status.trim();

    if (!name) return;

    const normalizedKey = name.toLowerCase().trim().replace(/[_\s-]+/g, '_');
    outcomeMetaByName.set(normalizedKey, {
      investigation_status: statusOption.isInvestigationStatus,
      escalated_investigation_status: statusOption.isEscalatedInvestigationStatus,
      outcome_color: statusOption.outcomeColor,
      outcome_icon: statusOption.iconName,
    });
    allowedStatusNames.add(name);
  });

  const filteredStatuses = Array.from(allowedStatusNames);

  const groupedStatuses = formatInvestigationStatuses(
    filteredStatuses,
    outcomeMetaByName,
    { includeOutcomes: true },
  );

  return {
    ...filters,
    // TODO - OPERATE1-3115: Clean up Piermont specific INV columns/filters
    actionedByAgents: assignedInvestigationAgents,
    assignedAgents,
    assignedAlertAgents,
    assignedInvestigationAgents,
    statuses: groupedStatuses,
    types: filteredTypes,
  };
};

export const reviewAndCloseInvestigation = async ({
  investigationToken,
  payload,
  typhonDomain,
}): Promise<InvestigationsQueueResponse> => {
  try {
    const investigations = await reviewAndClose({
      investigationToken,
      payload,
      typhonDomain,
    });

    return investigations;
  } catch (e) {
    const options = { cause: e?.response?.status };

    if (
      e?.response?.status === 403
      && e?.responseError?.details?.message === CURRENT_AGENT_CLOSE_ERROR
    ) {
      throw new Error(CURRENT_AGENT_CLOSE_ERROR, options);
    } else if (e?.responseError?.details?.message === INVESTIGATION_OPEN_ALERTS_CLOSE_ERROR) {
      throw new Error(INVESTIGATION_OPEN_ALERTS_CLOSE, options);
    } else if (e?.response?.status === 403) {
      throw new Error('Sorry, you don\'t have permissions to perform this action.', options);
    } else if (e?.response?.status === 404) {
      throw new Error('This Investigation could not be found', options);
    } else {
      throw new Error('Failed while closing Investigation', options);
    }
  }
};

export const fetchAssignableAgents = async ({
  typhonDomain,
  investigationTypes,
  investigationStateTypes,
}): Promise<InvestigationAgent[]> => {
  try {
    const agents = await getAssignableAgents({
      typhonDomain,
      investigationTypes,
      investigationStateTypes,
    });

    return agents;
  } catch (e) {
    const options = { cause: e?.response?.status };
    if (e?.response?.status === 403) {
      throw new Error('Sorry, you don\'t have permissions to perform this action.', options);
    } else if (e?.response?.status === 404) {
      throw new Error('No agent was found', options);
    } else {
      throw new Error('Failed to fetch agents', options);
    }
  }
};

export const assignInvestigationAgent = async ({
  typhonDomain,
  payload,
}: AssignAgentRequest): Promise<string> => {
  try {
    await assignAgent({ typhonDomain, payload });
    return payload?.agent_token;
  } catch (e) {
    const options = { cause: e?.response?.status };
    throw new Error('Failed to assign agent', options);
  }
};

export const unassignInvestigation = async ({
  typhonDomain,
  payload,
}: UnassignInvestigation): Promise<void> => {
  try {
    await unassign({ typhonDomain, payload });
  } catch (e) {
    const options = { cause: e?.response?.status };
    throw new Error('Failed to unassign agent', options);
  }
};
export const assignAlertAgent = async ({
  investigationToken,
  journeyApplicationToken,
  typhonDomain,
  payload,
}: AssignAlertAgentRequest): Promise<InvestigationAgent> => {
  try {
    await assignAlertAgentCall({
      investigationToken,
      journeyApplicationToken,
      payload,
      typhonDomain,
    });
    return payload?.agent;
  } catch (e) {
    const options = { cause: e?.response?.status };
    throw new Error('Failed to assign Alert agent', options);
  }
};

export const unassignAlertAgent = async ({
  investigationToken,
  journeyApplicationToken,
  typhonDomain,
}: UnassignAlertAgentRequest): Promise<boolean> => {
  try {
    await unassignAlertAgentCall({ investigationToken, journeyApplicationToken, typhonDomain });
    return true;
  } catch (e) {
    const options = { cause: e?.response?.status };
    throw new Error('Failed to unassign Alert agent', options);
  }
};

export const completeAlertReview = async ({
  investigationToken,
  journeyApplicationToken,
  payload,
  typhonDomain,
}: CompleteAlertReviewRequest): Promise<boolean> => {
  try {
    const response = await completeAlertReviewCall({
      investigationToken,
      journeyApplicationToken,
      payload,
      typhonDomain,
    });
    return response;
  } catch (e) {
    const options = { cause: e?.response?.status };
    throw new Error('Failed to submit Alert review', options);
  }
};

export const completeAlertManualReview = async ({
  alertManualReviewToken,
  investigationToken,
  journeyApplicationToken,
  payload,
  typhonDomain,
}: CompleteAlertManualReviewRequest): Promise<boolean> => {
  try {
    const response = await submitManualReview({
      alertManualReviewToken,
      investigationToken,
      journeyApplicationToken,
      payload,
      typhonDomain,
    });
    return response;
  } catch (e) {
    const options = { cause: e?.response?.status };
    throw new Error('Failed to submit Alert manual review', options);
  }
};

export const saveInvestigationNote = async ({
  investigationToken,
  payload,
  typhonDomain,
}) => {
  try {
    await submitInvestigationNote({
      investigationToken,
      payload,
      typhonDomain,
    });
  } catch (e) {
    const options = { cause: e?.response?.status };
    throw new Error('Failed to submit Investigation note', options);
  }
};

export const addEvidence = async ({
  investigationToken,
  payload,
  typhonDomain,
}) => {
  try {
    await addEvidenceToInvestigation({
      investigationToken,
      payload,
      typhonDomain,
    });
  } catch (e) {
    const options = { cause: e?.response?.status };
    throw new Error('Failed to add evidence to Investigation', options);
  }
};
